import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useNavigate,
} from "react-router-dom";

import {
  DataGrid,
} from "@mui/x-data-grid";

import {
  ThemeProvider,
  createTheme,
} from "@mui/material";

import {
  motion,
} from "framer-motion";

import {
  FiClock,
  FiEdit3,
  FiImage,
  FiPlus,
  FiTrash2,
  FiShoppingBag,
} from "react-icons/fi";

import {
  MdOutlineRestaurantMenu,
} from "react-icons/md";

import {
  getFilteredMealsWithSort,
  deleteMeal,
  ClearMeal,
} from "../../redux/actions/meals";

import {
  getAllCategorys,
} from "../../redux/actions/category";

import {
  Loader,
} from "../../components/utils";

import MealsFilter
  from "../../components/MealsFilter";

import {
  useStateContext,
} from "../../context/ContextProvider";

import {
  media,
} from "../../server";

import {
  truncateText,
} from "../hooks/TextVeiw";

import {
  localeTextGrid,
} from "../../constants";

import {
  toast,
} from "react-toastify";


function DAllMeals() {
  const {
    t,
  } = useTranslation();

  const dispatch =
    useDispatch();

  const navigate =
    useNavigate();

  const {
    currentDir,
  } = useStateContext();


  /* =====================================
      REDUX
  ====================================== */

  const {
    isLoading,
    FilltermealsWithSort,
    FilterpaginationWithSort,
  } = useSelector(
    (state) =>
      state.Meals
  );


  const {
    AllCategorys,
  } = useSelector(
    (state) =>
      state.Categorys
  );


  /* =====================================
      STATES
  ====================================== */

  const [
    deletingId,
    setDeletingId,
  ] = useState(null);


  const [
    paginationModel,
    setPaginationModel,
  ] = useState({
    pageSize: 10,
    page: 0,
  });


  const [
    filters,
    setFilters,
  ] = useState({
    search: "",
    categoryId: "",
    priceMin: "",
    priceMax: "",
    sortBy: "default",
  });


  const page =
    paginationModel.page;

  const limit =
    paginationModel.pageSize;


  /* =====================================
      SAFE DATA
  ====================================== */

  const meals =
    Array.isArray(
      FilltermealsWithSort
    )
      ? FilltermealsWithSort
      : [];


  const totalItems =
    FilterpaginationWithSort
      ?.totalItems ||
    0;


  /* =====================================
      GET MEALS
  ====================================== */

  useEffect(() => {

    dispatch(
      getFilteredMealsWithSort(
        page,
        limit,
        filters
      )
    );

  }, [
    page,
    limit,
    filters,
    dispatch,
  ]);


  /* =====================================
      GET CATEGORIES
  ====================================== */

  useEffect(() => {

    dispatch(
      getAllCategorys()
    );

  }, [
    dispatch,
  ]);


  /* =====================================
      FILTER
  ====================================== */

  const handleFilterChange =
    (newFilters) => {

      setFilters(
        newFilters
      );


      setPaginationModel(
        (prev) => ({
          ...prev,
          page: 0,
        })
      );

    };


  /* =====================================
      DELETE MEAL
  ====================================== */

  const deleteMealHandler =
    async (id) => {

      if (deletingId) {
        return;
      }


      try {

        setDeletingId(id);


        await dispatch(
          deleteMeal(id)
        );


        toast.success(
          <span
            className="
              font-kufi
              text-sm

              text-tcolor-4
              dark:text-tcolor-1
            "
          >
            {t(
              "Meal deleted successfully!"
            )}
          </span>
        );


        /*
         * إذا كانت آخر وجبة
         * في الصفحة الحالية.
         */

        if (
          meals.length === 1 &&
          page > 0
        ) {

          setPaginationModel(
            (prev) => ({
              ...prev,
              page:
                prev.page - 1,
            })
          );

          return;
        }


        await dispatch(
          getFilteredMealsWithSort(
            page,
            limit,
            filters
          )
        );

      } catch (error) {

        const errorMessage =
          error?.response
            ?.data?.message ||
          error?.message ||
          t(
            "An error occurred"
          );


        toast.error(
          <span
            className="
              font-kufi
              text-sm
            "
          >
            {errorMessage}
          </span>
        );

      } finally {

        setDeletingId(
          null
        );

      }
    };


  /* =====================================
      EDIT
  ====================================== */

  const updateMealHandler =
    (id) => {

      dispatch(
        ClearMeal()
      );


      navigate(
        `/EditmealPage/${id}`
      );

    };


  /* =====================================
      MUI THEME
  ====================================== */

  const theme =
    useMemo(
      () =>
        createTheme({
          direction:
            currentDir,

          typography: {
            fontFamily:
              "inherit",
          },
        }),
      [
        currentDir,
      ]
    );


  /* =====================================
      COLUMNS
  ====================================== */

  const columns =
    useMemo(
      () => [

        /* =============================
            IMAGE
        ============================== */

        {
          field:
            "Image",

          headerName:
            t("Image"),

          width: 95,

          minWidth: 95,

          sortable:
            false,

          filterable:
            false,

          align:
            "center",

          headerAlign:
            "center",

          renderCell: (
            params
          ) => (

            <div
              className="
                flex
                w-full
                h-full

                items-center
                justify-center
              "
            >

              {params.value ? (

                <img
                  src={`${media}${params.value}`}

                  alt={
                    params.row
                      ?.titleAr ||
                    t("Meal")
                  }

                  loading="lazy"

                  className="
                    w-[56px]
                    h-[56px]

                    rounded-[16px]

                    object-cover

                    border
                    border-color-1/15

                    shadow-sm
                  "
                />

              ) : (

                <div
                  className="
                    flex

                    w-[56px]
                    h-[56px]

                    items-center
                    justify-center

                    rounded-[16px]

                    bg-color-1/10

                    text-color-1
                  "
                >
                  <FiImage />
                </div>

              )}

            </div>

          ),
        },


        /* =============================
            ARABIC TITLE
        ============================== */

        {
          field:
            "titleAr",

          headerName:
            t(
              "Title (Ar)"
            ),

          minWidth: 155,

          flex: 0.75,

          sortable:
            false,

          filterable:
            false,

          renderCell: (
            params
          ) => (

            <div
              dir="rtl"

              className="
                flex
                w-full
                h-full

                items-center
              "
            >

              <span
                className="
                  block
                  w-full

                  overflow-hidden
                  text-ellipsis
                  whitespace-nowrap

                  font-kufi
                  font-semibold

                  text-[12px]

                  text-color-2
                  dark:text-white
                "

                title={
                  params.value ||
                  ""
                }
              >
                {
                  params.value ||
                  "-"
                }
              </span>

            </div>

          ),
        },


        /* =============================
            ENGLISH TITLE
        ============================== */

        {
          field:
            "titleEn",

          headerName:
            t(
              "Title (En)"
            ),

          minWidth: 165,

          flex: 0.8,

          sortable:
            false,

          filterable:
            false,

          renderCell: (
            params
          ) => (

            <div
              dir="ltr"

              className="
                flex
                w-full
                h-full

                items-center
              "
            >

              <span
                className="
                  block
                  w-full

                  overflow-hidden
                  text-ellipsis
                  whitespace-nowrap

                  font-medium

                  text-[12px]

                  text-color-2
                  dark:text-white/90
                "

                title={
                  params.value ||
                  ""
                }
              >
                {
                  params.value ||
                  "-"
                }
              </span>

            </div>

          ),
        },


        /* =============================
            ARABIC DESCRIPTION
        ============================== */

        {
          field:
            "textAr",

          headerName:
            t(
              "Text (Ar)"
            ),

          minWidth: 220,

          flex: 1.15,

          sortable:
            false,

          filterable:
            false,

          renderCell: (
            params
          ) => (

            <div
              dir="rtl"

              className="
                flex

                w-full
                h-full

                items-center
              "
            >

              <p
                className="
                  w-full

                  overflow-hidden

                  font-kufi

                  text-[10px]

                  leading-6

                  text-color-2/60
                  dark:text-white/55
                "
              >
                {truncateText(
                  params.value ||
                    "",
                  7
                )}
              </p>

            </div>

          ),
        },


        /* =============================
            ENGLISH DESCRIPTION
        ============================== */

        {
          field:
            "textEn",

          headerName:
            t(
              "Text (En)"
            ),

          minWidth: 220,

          flex: 1.15,

          sortable:
            false,

          filterable:
            false,

          renderCell: (
            params
          ) => (

            <div
              dir="ltr"

              className="
                flex

                w-full
                h-full

                items-center
              "
            >

              <p
                className="
                  w-full

                  overflow-hidden

                  text-[10px]

                  leading-6

                  text-color-2/60
                  dark:text-white/55
                "
              >
                {truncateText(
                  params.value ||
                    "",
                  7
                )}
              </p>

            </div>

          ),
        },


        /* =============================
            PRICE
        ============================== */

        {
          field:
            "price",

          headerName:
            t("price"),

          width: 110,

          minWidth: 110,

          sortable:
            false,

          filterable:
            false,

          align:
            "center",

          headerAlign:
            "center",

          renderCell: (
            params
          ) => {

            const priceValue =
              Number(
                params.value ||
                  0
              );


            return (
              <div
                className="
                  flex

                  w-full
                  h-full

                  items-center
                  justify-center

                  gap-1.5
                "
              >

                <span
                  className="
                    font-kufi
                    font-bold

                    text-[13px]

                    text-color-2
                    dark:text-white
                  "
                >
                  {priceValue.toLocaleString()}
                </span>


                <span
                  className="
                    font-kufi

                    text-[8px]
                    font-medium

                    text-color-1
                  "
                >
                  {t(
                    "EGP"
                  )}
                </span>

              </div>
            );
          },
        },


        /* =============================
            PREPARATION TIME
        ============================== */

        {
          field:
            "preparationTime",

          headerName:
            t(
              "Preparation time"
            ),

          width: 130,

          minWidth: 130,

          sortable:
            false,

          filterable:
            false,

          align:
            "center",

          headerAlign:
            "center",

          renderCell: (
            params
          ) => (

            <div
              className="
                flex

                w-full
                h-full

                items-center
                justify-center
              "
            >

              {params.value ? (

                <div
                  className="
                    inline-flex

                    items-center

                    gap-1.5

                    rounded-lg

                    bg-color-1/[0.07]

                    px-2.5
                    py-1.5

                    text-color-1
                  "
                >

                  <FiClock
                    size={11}
                  />


                  <span
                    className="
                      font-kufi

                      text-[9px]

                      whitespace-nowrap
                    "
                  >
                    {params.value}{" "}
                    {t(
                      "minutes"
                    )}
                  </span>

                </div>

              ) : (

                <span
                  className="
                    text-color-2/25
                    dark:text-white/25
                  "
                >
                  -
                </span>

              )}

            </div>

          ),
        },


        /* =============================
            ACTIONS
        ============================== */

        {
          field:
            "actions",

          headerName:
            t(
              "Actions"
            ),

          width: 195,

          minWidth: 195,

          sortable:
            false,

          filterable:
            false,

          align:
            "center",

          headerAlign:
            "center",

          renderCell: (
            params
          ) => {

            const id =
              params.row._id;

            const isDeleting =
              deletingId ===
              id;


            return (
              <div
                className="
                  flex

                  w-full
                  h-full

                  items-center
                  justify-center

                  gap-2
                "
              >

                {/* EDIT */}

                <motion.button
                  type="button"

                  whileTap={{
                    scale: 0.96,
                  }}

                  disabled={
                    Boolean(
                      deletingId
                    )
                  }

                  onClick={() =>
                    updateMealHandler(
                      id
                    )
                  }

                  className="
                    inline-flex

                    h-10

                    min-w-[78px]

                    items-center
                    justify-center

                    gap-2

                    rounded-xl

                    border
                    border-color-1/20

                    bg-color-1/10

                    px-3

                    font-kufi
                    text-[10px]

                    text-color-1

                    whitespace-nowrap

                    transition-all
                    duration-300

                    hover:bg-color-1
                    hover:text-white

                    disabled:opacity-40
                  "
                >

                  <FiEdit3
                    size={12}
                  />

                  {t(
                    "Edit"
                  )}

                </motion.button>


                {/* DELETE */}

                <motion.button
                  type="button"

                  whileTap={{
                    scale: 0.96,
                  }}

                  disabled={
                    Boolean(
                      deletingId
                    )
                  }

                  onClick={() =>
                    deleteMealHandler(
                      id
                    )
                  }

                  className="
                    inline-flex

                    h-10

                    min-w-[78px]

                    items-center
                    justify-center

                    gap-2

                    rounded-xl

                    border
                    border-red-500/15

                    bg-red-500/[0.05]

                    px-3

                    font-kufi
                    text-[10px]

                    text-red-500

                    whitespace-nowrap

                    transition-all
                    duration-300

                    hover:border-red-500
                    hover:bg-red-500
                    hover:text-white

                    disabled:opacity-40
                  "
                >

                  {isDeleting ? (

                    <span
                      className="
                        w-4
                        h-4

                        rounded-full

                        border-2
                        border-current
                        border-t-transparent

                        animate-spin
                      "
                    />

                  ) : (

                    <>
                      <FiTrash2
                        size={12}
                      />

                      {t(
                        "Delete"
                      )}
                    </>

                  )}

                </motion.button>

              </div>
            );
          },
        },

      ],

      [
        t,
        deletingId,
      ]
    );


  /* =====================================
      FIRST LOAD
  ====================================== */

  if (
    isLoading &&
    meals.length === 0
  ) {
    return <Loader />;
  }


  return (
    <motion.div
      initial={{
        opacity: 0,
        y: 15,
      }}

      animate={{
        opacity: 1,
        y: 0,
      }}

      className="
        w-full

        max-w-[1600px]

        mx-auto
      "
    >

      {/* =====================================
          HERO
      ====================================== */}

      <section
        className="
          relative

          overflow-hidden

          rounded-[2rem]

          bg-color-2

          px-5
          py-6

          md:px-8
          md:py-8

          mb-6

          text-white

          shadow-[0_18px_50px_rgba(68,37,20,.13)]
        "
      >

        <div
          className="
            absolute

            -top-28
            -end-20

            w-72
            h-72

            rounded-full

            bg-color-1/20

            blur-[100px]

            pointer-events-none
          "
        />


        <div
          className="
            absolute

            -bottom-32
            start-20

            w-64
            h-64

            rounded-full

            bg-color-1/10

            blur-[90px]

            pointer-events-none
          "
        />


        <div
          className="
            relative
            z-10

            flex
            flex-col

            md:flex-row
            md:items-center
            md:justify-between

            gap-5
          "
        >

          <div
            className="
              flex
              items-center

              gap-4
            "
          >

            <div
              className="
                flex

                w-14
                h-14

                shrink-0

                items-center
                justify-center

                rounded-2xl

                bg-color-1

                text-2xl

                shadow-[0_10px_30px_rgba(165,119,58,.28)]
              "
            >
              <MdOutlineRestaurantMenu />
            </div>


            <div>

              <span
                className="
                  font-kufi
                  text-[10px]

                  text-color-1
                "
              >
                {t(
                  "Meals Management"
                )}
              </span>


              <h1
                className="
                  mt-1

                  font-kufi
                  font-bold

                  text-xl
                  md:text-3xl
                "
              >
                {t(
                  "All Meals"
                )}
              </h1>


              <p
                className="
                  max-w-xl

                  mt-2

                  font-kufi

                  text-[10px]
                  md:text-xs

                  leading-6

                  text-white/50
                "
              >
                {t(
                  "Manage all Yaman restaurant meals prices categories and details"
                )}
              </p>

            </div>

          </div>


          {/* ADD MEAL */}

          <button
            type="button"

            onClick={() =>
              navigate(
                "/CreateMealPage"
              )
            }

            className="
              flex

              min-h-[44px]

              items-center
              justify-center

              gap-2

              rounded-full

              bg-color-1

              px-5

              font-kufi
              text-xs

              text-white

              transition-all

              hover:bg-white
              hover:text-color-2
            "
          >

            <FiPlus />

            {t(
              "Create Meal"
            )}

          </button>

        </div>

      </section>


      {/* =====================================
          COUNT + FILTER
      ====================================== */}

      <section
        className="
          grid

          grid-cols-1

          xl:grid-cols-[220px_1fr]

          gap-4

          mb-5
        "
      >

        {/* COUNT */}

        <motion.div
          whileHover={{
            y: -3,
          }}

          className="
            relative

            overflow-hidden

            rounded-[1.5rem]

            border
            border-color-2/10
            dark:border-white/10

            bg-back-primery
            dark:bg-back-darksecondary

            p-5

            shadow-[0_8px_25px_rgba(68,37,20,.04)]
          "
        >

          <div
            className="
              flex

              items-center
              justify-between
            "
          >

            <div>

              <span
                className="
                  block

                  text-3xl
                  font-bold

                  text-color-2
                  dark:text-white
                "
              >
                {totalItems}
              </span>


              <span
                className="
                  mt-1

                  block

                  font-kufi
                  font-semibold

                  text-[11px]

                  text-color-2/65
                  dark:text-white/60
                "
              >
                {t(
                  "Total Meals"
                )}
              </span>


              <span
                className="
                  mt-2

                  block

                  font-kufi
                  text-[9px]

                  text-color-2/35
                  dark:text-white/30
                "
              >
                {t(
                  "Restaurant menu meals"
                )}
              </span>

            </div>


            <div
              className="
                flex

                w-11
                h-11

                items-center
                justify-center

                rounded-2xl

                bg-color-1/10

                text-color-1
              "
            >
              <FiShoppingBag />
            </div>

          </div>


          <span
            className="
              absolute

              bottom-0
              start-0

              w-14
              h-[3px]

              bg-color-1
            "
          />

        </motion.div>


        {/* FILTER */}

        <div
          className="
            rounded-[1.5rem]

            border
            border-color-2/10
            dark:border-white/10

            bg-back-primery
            dark:bg-back-darksecondary

            p-4
            md:p-5

            shadow-[0_8px_25px_rgba(68,37,20,.04)]
          "
        >

          <div
            className="
              flex

              items-center

              gap-3

              mb-4
            "
          >

            <div
              className="
                flex

                w-9
                h-9

                items-center
                justify-center

                rounded-xl

                bg-color-1/10

                text-color-1
              "
            >
              <MdOutlineRestaurantMenu />
            </div>


            <div>

              <h2
                className="
                  font-kufi
                  font-semibold

                  text-xs

                  text-color-2
                  dark:text-white
                "
              >
                {t(
                  "Filter Meals"
                )}
              </h2>


              <p
                className="
                  mt-1

                  font-kufi
                  text-[9px]

                  text-color-2/40
                  dark:text-white/30
                "
              >
                {t(
                  "Search and filter restaurant meals"
                )}
              </p>

            </div>

          </div>


          <MealsFilter
            onFilterChange={
              handleFilterChange
            }

            categories={
              AllCategorys ||
              []
            }

            initialFilters={
              filters
            }
          />

        </div>

      </section>


      {/* =====================================
          TABLE
      ====================================== */}

      <section
        className="
          overflow-hidden

          rounded-[1.75rem]

          border
          border-color-2/10
          dark:border-white/10

          bg-back-primery
          dark:bg-back-darksecondary

          shadow-[0_12px_35px_rgba(68,37,20,.05)]
        "
      >

        {/* TABLE HEADER */}

        <div
          className="
            flex

            items-center

            gap-3

            px-5
            md:px-6

            py-5

            border-b
            border-color-2/10
            dark:border-white/10
          "
        >

          <div
            className="
              flex

              w-10
              h-10

              items-center
              justify-center

              rounded-xl

              bg-color-1/10

              text-color-1
            "
          >
            <MdOutlineRestaurantMenu />
          </div>


          <div>

            <h2
              className="
                font-kufi
                font-bold

                text-sm

                text-color-2
                dark:text-white
              "
            >
              {t(
                "Meals List"
              )}
            </h2>


            <p
              className="
                mt-1

                font-kufi
                text-[9px]

                text-color-2/45
                dark:text-white/35
              "
            >
              {t(
                "View edit and manage all restaurant meals"
              )}
            </p>

          </div>

        </div>


        {/* DATA GRID */}

        <ThemeProvider
          theme={theme}
        >

          <div
            className="
              w-full

              overflow-x-auto

              p-3
              md:p-5
            "
          >

            <DataGrid
              rows={
                meals
              }

              columns={
                columns
              }

              getRowId={(
                row
              ) =>
                row._id
              }

              disableRowSelectionOnClick

              disableColumnSelector

              pagination

              paginationMode="server"

              rowCount={
                totalItems
              }

              paginationModel={
                paginationModel
              }

              onPaginationModelChange={
                setPaginationModel
              }

              pageSizeOptions={[
                10,
                20,
                50,
              ]}

              localeText={
                localeTextGrid
              }

              loading={
                isLoading
              }

              rowHeight={
                76
              }

              columnHeaderHeight={
                56
              }

              sx={{
                width:
                  "100%",

                minWidth:
                  "1350px",

                border:
                  "none",

                backgroundColor:
                  "transparent",


                /* Header */

                "& .MuiDataGrid-columnHeaders":
                  {
                    backgroundColor:
                      "#fff",

                    borderBottom:
                      "1px solid rgba(68,37,20,.09)",
                  },


                "& .MuiDataGrid-columnHeader":
                  {
                    paddingLeft:
                      "14px",

                    paddingRight:
                      "14px",

                    outline:
                      "none !important",
                  },


                "& .MuiDataGrid-columnHeaderTitleContainer":
                  {
                    height:
                      "100%",

                    display:
                      "flex",

                    alignItems:
                      "center",
                  },


                "& .MuiDataGrid-columnHeaderTitle":
                  {
                    fontSize:
                      "10px",

                    fontWeight:
                      "700",

                    lineHeight:
                      "1.4",

                    whiteSpace:
                      "nowrap",
                  },


                /* Cells */

                "& .MuiDataGrid-cell":
                  {
                    paddingLeft:
                      "14px",

                    paddingRight:
                      "14px",

                    borderBottom:
                      "1px solid rgba(68,37,20,.06)",

                    outline:
                      "none !important",

                    overflow:
                      "hidden",

                    display:
                      "flex",

                    alignItems:
                      "center",
                  },


                /* Rows */

                "& .MuiDataGrid-row":
                  {
                    transition:
                      "background-color .2s ease",
                  },


                "& .MuiDataGrid-row:hover":
                  {
                    backgroundColor:
                      "rgba(165,119,58,.045)",
                  },


                "& .MuiDataGrid-row:last-child .MuiDataGrid-cell":
                  {
                    borderBottom:
                      "none",
                  },


                /* Footer */

                "& .MuiDataGrid-footerContainer":
                  {
                    minHeight:
                      "58px",

                    borderTop:
                      "1px solid rgba(68,37,20,.08)",
                  },


                /* Focus */

                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus":
                  {
                    outline:
                      "none",
                  },
              }}
            />

          </div>

        </ThemeProvider>

      </section>

    </motion.div>
  );
}


export default DAllMeals;